/**
 * Mount Rigi Header
 * Sticky header, desktop dropdowns and mobile drawer
 * Version: 5.0
 */

(() => {
  const HEADER_SELECTOR =
    '[data-mr-header-shell]';

  const INITIALIZED_ATTRIBUTE =
    'data-mr-header-initialized';

  const MOBILE_BREAKPOINT =
    '(max-width: 989px)';

  const DESKTOP_CLOSE_DELAY = 150;

  const getFocusableElements = (container) => {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',')
      )
    ).filter((element) => {
      return (
        element instanceof HTMLElement &&
        !element.hasAttribute('hidden') &&
        element.offsetParent !== null
      );
    });
  };

  const initializeHeader = (shell) => {
    if (!(shell instanceof HTMLElement)) return;

    if (
      shell.getAttribute(
        INITIALIZED_ATTRIBUTE
      ) === 'true'
    ) {
      return;
    }

    shell.setAttribute(
      INITIALIZED_ATTRIBUTE,
      'true'
    );

    const header = shell.querySelector(
      '[data-mr-header]'
    );

    const announcement = shell.querySelector(
      '[data-mr-announcement]'
    );

    const desktopNavigation =
      shell.querySelector(
        '[data-mr-desktop-navigation]'
      );

    const desktopMenuItems = Array.from(
      shell.querySelectorAll(
        '[data-mr-menu-item]'
      )
    );

    const mobileMenu = shell.querySelector(
      '[data-mr-mobile-menu]'
    );

    const mobileOpenButton =
      shell.querySelector(
        '[data-mr-mobile-menu-open]'
      );

    const mobileCloseButton =
      shell.querySelector(
        '[data-mr-mobile-menu-close]'
      );

    const mobileOverlay = shell.querySelector(
      '[data-mr-mobile-menu-overlay]'
    );

    const mobileQuery =
      window.matchMedia(MOBILE_BREAKPOINT);

    const drawerId =
      `mobile-menu-${mobileMenu?.id || 'main'}`;

    let mobileMenuIsOpen = false;
    let lastFocusedElement = null;
    let desktopCloseTimer = null;

    const getDropdown = (item) => {
      return item?.querySelector(
        '[data-mr-dropdown]'
      ) || null;
    };

    const getMenuLink = (item) => {
      return item?.querySelector(
        '[data-mr-menu-link]'
      ) || null;
    };

    const cancelDesktopClose = () => {
      if (desktopCloseTimer === null) return;

      window.clearTimeout(
        desktopCloseTimer
      );

      desktopCloseTimer = null;
    };

    const persistentDesktopItem =
      desktopMenuItems.find((item) => {
        return item.hasAttribute(
          'data-mr-persistent-open'
        );
      }) || null;

    const setDesktopItemState = (
      item,
      shouldOpen
    ) => {
      if (!(item instanceof HTMLElement)) {
        return;
      }

      const dropdown = getDropdown(item);
      const menuLink = getMenuLink(item);

      if (!dropdown) return;

      item.classList.toggle(
        'is-open',
        shouldOpen
      );

      dropdown.setAttribute(
        'aria-hidden',
        shouldOpen ? 'false' : 'true'
      );

      menuLink?.setAttribute(
        'aria-expanded',
        shouldOpen ? 'true' : 'false'
      );
    };

    const restorePersistentDesktopItem = () => {
      if (
        persistentDesktopItem &&
        !mobileQuery.matches
      ) {
        setDesktopItemState(
          persistentDesktopItem,
          true
        );
      }
    };

    const closeAllDesktopItems = (
      except = null,
      restorePersistent = true
    ) => {
      cancelDesktopClose();

      desktopMenuItems.forEach((item) => {
        if (item !== except) {
          setDesktopItemState(
            item,
            false
          );
        }
      });

      if (
        restorePersistent &&
        persistentDesktopItem !== except
      ) {
        restorePersistentDesktopItem();
      }
    };

    const openDesktopItem = (item) => {
      if (
        !(item instanceof HTMLElement) ||
        mobileQuery.matches
      ) {
        return;
      }

      if (!getDropdown(item)) return;

      cancelDesktopClose();

      closeAllDesktopItems(
        item,
        false
      );

      setDesktopItemState(
        item,
        true
      );
    };

    const scheduleDesktopClose = () => {
      cancelDesktopClose();

      desktopCloseTimer =
        window.setTimeout(() => {
          closeAllDesktopItems(
            null,
            true
          );
        }, DESKTOP_CLOSE_DELAY);
    };

    desktopMenuItems.forEach((item) => {
      if (!getDropdown(item)) return;

      item.addEventListener(
        'mouseenter',
        () => openDesktopItem(item)
      );

      item.addEventListener(
        'focusin',
        () => openDesktopItem(item)
      );

      item.addEventListener(
        'focusout',
        (event) => {
          const nextTarget =
            event.relatedTarget;

          if (
            nextTarget instanceof Node &&
            header?.contains(nextTarget)
          ) {
            return;
          }

          scheduleDesktopClose();
        }
      );
    });

    header?.addEventListener(
      'mouseenter',
      cancelDesktopClose
    );

    header?.addEventListener(
      'mouseleave',
      scheduleDesktopClose
    );

    desktopNavigation?.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;

        closeAllDesktopItems();

        if (
          document.activeElement
          instanceof HTMLElement
        ) {
          document.activeElement.blur();
        }
      }
    );

    const setAnnouncementHeight = () => {
      const height =
        announcement?.offsetHeight || 0;

      shell.style.setProperty(
        '--mr-current-announcement-height',
        `${height}px`
      );
    };

    const performOpenMobileMenu = () => {
      if (
        !mobileMenu ||
        !mobileOpenButton ||
        !mobileQuery.matches
      ) {
        return;
      }

      closeAllDesktopItems(
        null,
        false
      );

      lastFocusedElement =
        document.activeElement
        instanceof HTMLElement
          ? document.activeElement
          : mobileOpenButton;

      mobileMenuIsOpen = true;

      mobileMenu.classList.add(
        'is-open'
      );

      mobileMenu.setAttribute(
        'aria-hidden',
        'false'
      );

      mobileOverlay?.classList.add(
        'is-visible'
      );

      mobileOverlay?.removeAttribute(
        'tabindex'
      );

      mobileOpenButton.setAttribute(
        'aria-expanded',
        'true'
      );

      mobileOpenButton.setAttribute(
        'aria-label',
        'Close menu'
      );

      window.requestAnimationFrame(() => {
        mobileCloseButton?.focus({
          preventScroll: true
        });
      });
    };

    const performCloseMobileMenu = ({
      restoreFocus = true
    } = {}) => {
      if (
        !mobileMenu ||
        !mobileOpenButton
      ) {
        return;
      }

      mobileMenuIsOpen = false;

      mobileMenu.classList.remove(
        'is-open'
      );

      mobileMenu.setAttribute(
        'aria-hidden',
        'true'
      );

      mobileOverlay?.classList.remove(
        'is-visible'
      );

      mobileOverlay?.setAttribute(
        'tabindex',
        '-1'
      );

      mobileOpenButton.setAttribute(
        'aria-expanded',
        'false'
      );

      mobileOpenButton.setAttribute(
        'aria-label',
        'Open menu'
      );

      if (
        restoreFocus &&
        lastFocusedElement
        instanceof HTMLElement
      ) {
        lastFocusedElement.focus({
          preventScroll: true
        });
      }
    };

    window.MRDrawerManager?.register(
      drawerId,
      {
        open: performOpenMobileMenu,
        close: performCloseMobileMenu,
        isOpen: () => mobileMenuIsOpen
      }
    );

    mobileOpenButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (window.MRDrawerManager) {
          window.MRDrawerManager.toggle(
            drawerId
          );
        } else if (mobileMenuIsOpen) {
          performCloseMobileMenu();
        } else {
          performOpenMobileMenu();
        }
      }
    );

    mobileCloseButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();

        if (window.MRDrawerManager) {
          window.MRDrawerManager.close(
            drawerId
          );
        } else {
          performCloseMobileMenu();
        }
      }
    );

    mobileOverlay?.addEventListener(
      'click',
      () => {
        if (window.MRDrawerManager) {
          window.MRDrawerManager.close(
            drawerId
          );
        } else {
          performCloseMobileMenu();
        }
      }
    );

    mobileMenu
      ?.querySelectorAll('a[href]')
      .forEach((link) => {
        link.addEventListener(
          'click',
          () => {
            if (window.MRDrawerManager) {
              window.MRDrawerManager.close(
                drawerId,
                {
                  restoreFocus: false
                }
              );
            } else {
              performCloseMobileMenu({
                restoreFocus: false
              });
            }
          }
        );
      });

    mobileMenu?.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key !== 'Tab' ||
          !mobileMenuIsOpen
        ) {
          return;
        }

        const focusableElements =
          getFocusableElements(
            mobileMenu
          );

        if (
          focusableElements.length === 0
        ) {
          return;
        }

        const firstElement =
          focusableElements[0];

        const lastElement =
          focusableElements[
            focusableElements.length - 1
          ];

        if (
          event.shiftKey &&
          document.activeElement ===
            firstElement
        ) {
          event.preventDefault();
          lastElement.focus();
        }

        if (
          !event.shiftKey &&
          document.activeElement ===
            lastElement
        ) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    );

    const updateScrollState = () => {
      const threshold = Math.max(
        12,
        announcement?.offsetHeight || 0
      );

      shell.classList.toggle(
        'is-scrolled',
        window.scrollY > threshold
      );
    };

    const handleViewportChange = () => {
      setAnnouncementHeight();

      if (
        !mobileQuery.matches &&
        mobileMenuIsOpen
      ) {
        if (window.MRDrawerManager) {
          window.MRDrawerManager.close(
            drawerId,
            {
              restoreFocus: false
            }
          );
        } else {
          performCloseMobileMenu({
            restoreFocus: false
          });
        }
      }

      closeAllDesktopItems(
        null,
        true
      );
    };

    document.addEventListener(
      'click',
      (event) => {
        if (
          !(event.target instanceof Node)
        ) {
          return;
        }

        if (!shell.contains(event.target)) {
          closeAllDesktopItems();
        }
      }
    );

    window.addEventListener(
      'scroll',
      updateScrollState,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      setAnnouncementHeight,
      { passive: true }
    );

    mobileQuery.addEventListener?.(
      'change',
      handleViewportChange
    );

    setAnnouncementHeight();
    updateScrollState();
    restorePersistentDesktopItem();
  };

  const initializeAllHeaders = (
    root = document
  ) => {
    if (
      root instanceof HTMLElement &&
      root.matches(HEADER_SELECTOR)
    ) {
      initializeHeader(root);
    }

    root
      .querySelectorAll?.(
        HEADER_SELECTOR
      )
      .forEach(initializeHeader);
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAllHeaders()
    );
  } else {
    initializeAllHeaders();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeAllHeaders(event.target);
    }
  );
})();
